import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { WidgetManifest, WidgetVersionsIndex } from '@/models/widget';
import { widgetVersionPath } from '@/utils/widgetVersions';

/**
 * Absolute base URL of the widgets directory (note the trailing `/widgets/`).
 * Each version lives in a subdirectory of it, and its module entry — the one
 * script the preview loads and the snippet embeds — resolves against that.
 *
 * This must keep pointing at the portal origin even in dev: a widget bundle
 * derives its API bridge origin from its own URL and skips the bridge when that
 * origin equals the page's, so serving a bundle from this app's origin would
 * silently disable its API access. The portal origin is set per mode in the
 * repo root `.env.<mode>` files.
 */
export const WIDGET_BASE_URL = `${import.meta.env.VITE_WIDGET_ORIGIN}/widgets/`;

/**
 * Where `versions.json` and each version's manifest are read from. The dev
 * server routes both through the Vite proxy to make the requests same-origin.
 *
 * `import.meta.env.DEV`, deliberately NOT `isDevelopment`: that proxy exists
 * only while `vite dev` is running, whereas `isDevelopment` is
 * `MODE !== 'production'` and so is ALSO true for a staging build. The deployed
 * staging app therefore asked S3 for the proxy path and got `403 AccessDenied`
 * — a same-origin missing key that reads like a CORS failure but is not one.
 * `DEV` is false for every `vite build`, staging included.
 *
 * Both built modes read these cross-origin, so they depend on the widgets
 * distribution's `access-control-allow-origin` header — see
 * `docs/infra/2026-09-02-widgets-manifest-cors.md`.
 */
const WIDGETS_FETCH_ROOT = import.meta.env.DEV
    ? '/widgets-manifest/'
    : WIDGET_BASE_URL;

export const widgetManifestApi = createApi({
    reducerPath: 'widgetManifestApi',
    baseQuery: fetchBaseQuery({ baseUrl: '' }),
    endpoints: (builder) => ({
        getWidgetVersions: builder.query<WidgetVersionsIndex, void>({
            query: () => `${WIDGETS_FETCH_ROOT}versions.json`,
        }),
        getWidgetManifest: builder.query<WidgetManifest, string>({
            query: (version) =>
                widgetVersionPath(WIDGETS_FETCH_ROOT, version, 'manifest.json'),
        }),
    }),
});

export const { useGetWidgetVersionsQuery, useGetWidgetManifestQuery } =
    widgetManifestApi;
